import React, { useEffect, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import i18n from 'i18next';
import InfoIcon from '@mui/icons-material/Info';

import { Breadcrumbs } from 'uikit/layouts';
import { Button } from 'uikit/buttons';
import { Typo } from 'uikit/content';

import { useGlobalQueries } from '@/exceed';

import VideoChat from './videoChat';
import VideoPlayer from './videoPlayer';
import VideoContent from './videoContent';
import VideoCountdown from './videoCountdown';
import VideoTopicWidget from './videoTopicWidget';

import getUpcomingVideoInTopic from '../helpers/getUpcomingVideoInTopic';
import getVideoBreadcrumbs from '../helpers/getVideoBreadcrumbs';
import getLearnerVideoSource from '../helpers/getLearnerVideoSource';
import getIsUserPublic from '../helpers/getIsUserPublic';

import useTitleWithAccountName from '../hooks/useTitleWithAccountName';

import styles from '../styles/video.module.scss';
import getIsAgendaItemCurrentlyLive from '../helpers/getIsAgendaItemCurrentlyLive';

/* eslint-disable camelcase */
const Video = ({
  selectedAgendaItem,
  viewAgendaItem,
  video,
  relatedVideos,
  screenSize,
  dropEnrollment,
  reportVideo,
  favouriteVideo,
  unfavouriteVideo,
  relatedCourses,
  createEnrollment,
  updateEnrollment,
  updateReview,
  agendaItems,
  shouldHideCustomControls,
  isSettingEnrollmentStatus,
  setEnrollmentStatus,
}) => {
  const {
    author,
    breadcrumbs = [],
    caption,
    description,
    difficulty,
    duration,
    end_at: endAt,
    enrollment_metadata: enrollmentData,
    enrollment_waitlist_status: enrollmentStatus,
    has_chat_enabled: hasChat,
    id: videoId,
    is_favorited,
    locale,
    locales,
    name,
    published_at,
    reputation_points: skillsReputation,
    spot_in_waitlist: waitlistSpot,
    start_at: startAt,
    tags = [],
    topic,
    total_waitlisted: waitlistTotal,
    tribe_chat_identifier: chatPath,
    vote_total,
    vote_user,
    learning_objectives,
    display_learning_objectives,
  } = video?.data || {};

  const { id, type } = useParams();
  const isInLiveMode = type === 'live';
  const isEnrolled = enrollmentStatus === 'enrolled';

  const nextVideo = getUpcomingVideoInTopic({
    items: relatedVideos.data,
    currentVideoId: id,
  });

  const allSpeakers = agendaItems?.flatMap(({ speakers }) => speakers || []);

  // get duplicate-free list of speakers
  const speakers = allSpeakers && [
    ...new Map(allSpeakers.map((speaker) => [speaker.id, speaker])).values(),
  ];

  const [hasPreviewFinished, setHasPreviewFinished] = useState(false);
  const determineVideoUrl = useCallback(
    () =>
      getLearnerVideoSource({
        type,
        video,
        agendaItem: selectedAgendaItem,
        hasPreviewFinished,
      }),
    [type, video?.data?.id, isEnrolled, selectedAgendaItem?.id, hasPreviewFinished],
  );

  const [currentVideo, setCurrentVideo] = useState('');

  const onPreviewVideoEnded = () => {
    if (isInLiveMode && currentVideo === video.data.preview_video) {
      setHasPreviewFinished(true);
    }
  };

  useTitleWithAccountName(name);

  useEffect(() => {
    setCurrentVideo(determineVideoUrl());

    if (!selectedAgendaItem?.id) return;

    const isLive = getIsAgendaItemCurrentlyLive({ agendaItem: selectedAgendaItem });
    if (!isLive) {
      const interval = setInterval(() => {
        if (getIsAgendaItemCurrentlyLive({ agendaItem: selectedAgendaItem })) {
          setCurrentVideo(determineVideoUrl());
        }
      }, 1000);
      return () => clearInterval(interval);
    }
  }, [type, video?.data?.id, isEnrolled, selectedAgendaItem?.id, hasPreviewFinished]);

  const globalQueries = useGlobalQueries();
  const isPublicUser = getIsUserPublic(globalQueries);
  const { currentUser, learnerSettings } = globalQueries;
  const currentUsersLocale = currentUser.data?.locale;
  const usersVideoLocal = locales?.find((localeItem) => localeItem.locale === currentUsersLocale);
  const shouldDisplayLocaleWarning = locale !== currentUsersLocale && usersVideoLocal;
  const availableLocales = learnerSettings?.data?.locales || [];
  const { name: usersLocaleName } =
    availableLocales.find(({ key }) => key === currentUsersLocale) || {};

  return (
    <div className={styles.container}>
      <Breadcrumbs
        className={styles.breadcrumbs}
        breadcrumbs={getVideoBreadcrumbs({ breadcrumbs, currentLocation: video.data })}
      />
      {shouldDisplayLocaleWarning && (
        <div className={styles.localeWarning}>
          <InfoIcon sx={{ height: 18, width: 18 }} />
          <Typo color="secondary" element="span" variant="s02">
            {i18n.t('locale-warning', { locale: usersLocaleName })}
          </Typo>
          <Button
            className={styles.viewActivity}
            to={`/student/videos/live/${usersVideoLocal.id}`}
            variant="link"
            text={i18n.t('view-activity')}
          />
        </div>
      )}
      {startAt && endAt && (
        <VideoCountdown
          hasBorder={!currentVideo}
          endAt={endAt}
          enrollmentStatus={enrollmentStatus}
          isEnrolled={isEnrolled}
          isSettingEnrollmentStatus={isSettingEnrollmentStatus}
          setEnrollmentStatus={setEnrollmentStatus}
          startAt={startAt}
          waitlistSpot={waitlistSpot}
          waitlistTotal={waitlistTotal}
        />
      )}

      {currentVideo && (
        <div className={styles.header}>
          <VideoPlayer
            className={styles.player}
            id={id}
            video={currentVideo}
            screenSize={screenSize}
            createEnrollment={createEnrollment}
            updateEnrollment={updateEnrollment}
            enrollmentData={enrollmentData}
            caption={caption}
            upNextVideo={nextVideo}
            shouldHideMenu
            onEnded={onPreviewVideoEnded}
            shouldHideCustomControls={
              shouldHideCustomControls !== undefined
                ? shouldHideCustomControls
                : isInLiveMode && currentVideo !== video.data.preview_video
            }
            shouldNotEnroll={isPublicUser || isInLiveMode}
            selectedAgendaItem={selectedAgendaItem}
          />
          {relatedVideos?.data.length > 1 && (
            <VideoTopicWidget name={topic?.name} currentVideoId={id} topic={relatedVideos} />
          )}
          {hasChat && <VideoChat chatPath={chatPath} />}
        </div>
      )}

      <VideoContent
        enrollmentStatus={enrollmentStatus}
        endAt={endAt}
        skills={tags.filter((tag) => tag.is_skill_item)}
        skillsReputation={skillsReputation}
        speakers={speakers}
        startAt={startAt}
        title={name}
        description={description}
        duration={duration}
        is_favorited={is_favorited}
        vote_total={vote_total}
        vote_user={vote_user}
        difficulty={difficulty}
        dropEnrollment={dropEnrollment}
        reportVideo={() => reportVideo({ id: videoId })}
        favouriteVideo={favouriteVideo}
        unfavouriteVideo={unfavouriteVideo}
        relatedCourses={relatedCourses}
        updateReview={updateReview}
        agendaItems={agendaItems}
        selectedAgendaItem={selectedAgendaItem}
        viewAgendaItem={viewAgendaItem}
        published_at={published_at}
        author={author}
        isEnrolled={isEnrolled}
        isSettingEnrollmentStatus={isSettingEnrollmentStatus}
        setEnrollmentStatus={setEnrollmentStatus}
        type={type}
        waitlistSpot={waitlistSpot}
        waitlistTotal={waitlistTotal}
        learning_objectives={learning_objectives}
        display_learning_objectives={display_learning_objectives}
        enrollmentData={enrollmentData}
      />
    </div>
  );
};

Video.propTypes = {};

export default React.memo(Video);
